import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Loader2, ArrowRightLeft } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { StatusPill } from "@/components/StatusPill";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { getErrorMessage } from "@/lib/errors";

const WRITE_ROLES = ["ORG_ADMIN", "PROCUREMENT", "STORES"];

export default function Transfers() {
  const { user } = useAuth();
  const canWrite = WRITE_ROLES.includes(user?.role);
  const [transfers, setTransfers] = useState([]);
  const [items, setItems] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [tRes, itemRes, whRes] = await Promise.all([api.get("/inventory/stock-transfers"), api.get("/inventory/items"), api.get("/inventory/warehouses")]);
      setTransfers(tRes.data); setItems(itemRes.data); setWarehouses(whRes.data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const itemName = (id) => { const i = items.find((it) => it.id === id); return i ? `${i.code} — ${i.name}` : id; };
  const warehouseName = (id) => warehouses.find((w) => w.id === id)?.name || id;

  const post = async (t) => {
    setBusyId(t.id);
    try {
      await api.post(`/inventory/stock-transfers/${t.id}/post`);
      toast.success("Stock transfer posted");
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="transfers-page">
      <PageHeader title="Stock Transfers" description="Move stock between warehouses of the same company at the source's average cost." action={canWrite && <CreateTransferDialog items={items} warehouses={warehouses} onCreated={load} />} />
      <div className="rounded-xl border border-border bg-card">
        {!loading && transfers.length === 0 ? (
          <EmptyState title="No stock transfers yet" testId="transfers-empty-state" />
        ) : (
          <Table>
            <TableHeader><TableRow><TableHead>Item</TableHead><TableHead>From</TableHead><TableHead>To</TableHead><TableHead className="text-right">Qty</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {transfers.map((t) => (
                <TableRow key={t.id} data-testid={`transfer-row-${t.id}`}>
                  <TableCell className="text-foreground">{itemName(t.item_id)}</TableCell>
                  <TableCell className="text-muted-foreground">{warehouseName(t.from_warehouse_id)}</TableCell>
                  <TableCell className="text-muted-foreground">{warehouseName(t.to_warehouse_id)}</TableCell>
                  <TableCell className="text-right font-mono text-xs text-foreground">{t.quantity}</TableCell>
                  <TableCell><StatusPill value={t.status} /></TableCell>
                  <TableCell className="text-right">
                    {canWrite && t.status === "DRAFT" && (
                      <Button size="sm" variant="outline" disabled={busyId === t.id} onClick={() => post(t)} data-testid={`post-transfer-button-${t.id}`}>
                        <ArrowRightLeft className="h-3.5 w-3.5" /> Post
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}

function CreateTransferDialog({ items, warehouses, onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ from_warehouse_id: "", to_warehouse_id: "", item_id: "", quantity: "", transfer_date: "" });
  const [saving, setSaving] = useState(false);
  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/inventory/stock-transfers", form);
      toast.success("Stock transfer created");
      setForm({ from_warehouse_id: "", to_warehouse_id: "", item_id: "", quantity: "", transfer_date: "" });
      setOpen(false);
      onCreated?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="create-transfer-button"><Plus className="h-4 w-4" /> New Transfer</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>New Stock Transfer</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3" data-testid="create-transfer-form">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>From warehouse</Label>
              <Select required value={form.from_warehouse_id} onValueChange={(v) => setForm({ ...form, from_warehouse_id: v })}>
                <SelectTrigger data-testid="transfer-from-select" className="mt-1.5"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{warehouses.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>To warehouse</Label>
              <Select required value={form.to_warehouse_id} onValueChange={(v) => setForm({ ...form, to_warehouse_id: v })}>
                <SelectTrigger data-testid="transfer-to-select" className="mt-1.5"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{warehouses.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Item</Label>
            <Select required value={form.item_id} onValueChange={(v) => setForm({ ...form, item_id: v })}>
              <SelectTrigger data-testid="transfer-item-select" className="mt-1.5"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{items.map((it) => <SelectItem key={it.id} value={it.id}>{it.code} — {it.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Qty</Label><Input required type="number" step="0.001" data-testid="transfer-qty-input" value={form.quantity} onChange={set("quantity")} className="mt-1.5" /></div>
            <div><Label>Date</Label><Input required type="date" data-testid="transfer-date-input" value={form.transfer_date} onChange={set("transfer_date")} className="mt-1.5" /></div>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={saving} data-testid="create-transfer-submit-button">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Create</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
